import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Hub } from 'aws-amplify/utils';
import { useCognitoAuthContext } from '@caplend/cognito-auth';
import { reapplyAmplifyOAuthConfig, oauthLogout } from './auth/configureAmplify';
import AccessTokenOutlet from './components/Outlets/AccessTokenOutlet';
import OnboardingOutlet from './components/Outlets/OnboardingOutlet';
import Login from './pages/Login';
import Register from './pages/Register';
import ConfirmEmail from './pages/ConfirmEmail';
import PhoneVerification from './pages/PhoneVerification';
import ChooseUserType from './pages/ChooseUserType';
import CollectData from './pages/CollectData';
import Financial from './pages/Financial';
import Compliance from './pages/Compliance';
import CollectCompanyPersonData from './pages/CollectCompanyPersonData';
import TermsAndConditions from './pages/TermsAndConditions';
import BlindPayAccept from './pages/BlindPayAccept';
import VerificationPending from './pages/VerificationPending';
import UnderReview from './pages/UnderReview';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Account from './pages/Account';
import SendMoney from './pages/SendMoney';
import TransactionProgress from './pages/TransactionProgress';
import AddRecipientForm from './pages/AddRecipientForm';
import SelectCurrency from './pages/SelectCurrency';
import SelectRecipientType from './pages/SelectRecipientType';
import SelectRail from './pages/SelectRail';
import Recipients from './pages/Recipients';
import RecipientDetail from './pages/RecipientDetail'; // eslint-disable-line no-unused-vars -- used in Route
import TransactionHistory from './pages/TransactionHistory';
import TransactionDetail from './pages/TransactionDetail';
import NotFound from './pages/NotFound';
import Footer from './components/Footer';
import ForgotPassword from './pages/ForgotPassword';
import EmailStartKycProcess from './pages/EmailStartKycProcess'; // eslint-disable-line no-unused-vars -- used in Route
import VerificationLinkSent from './pages/VerificationLinkSent'; // eslint-disable-line no-unused-vars -- used in Route
import HansePayLoader from './components/HansePayLoader';
import * as userActions from './store/actions/user';
import * as configActions from './store/actions/config';

function OAuthCallbackHandler() {
  const navigate = useNavigate();
  const { setHasAccessToken } = useCognitoAuthContext();
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', (data) => {
      if (data.payload?.event === 'signInWithRedirect') {
        setHasAccessToken(true);
        navigate('/verify-phone-number', { replace: true });
      }
    });
    return () => unsubscribe();
  }, [setHasAccessToken, navigate]);
  return null;
}

function OAuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description') ?? '';

    if (!error) return;

    const decoded = decodeURIComponent(errorDescription.replace(/\+/g, ' '));
    const isAccountExists = /already exists/i.test(decoded) || /sign in with your email and password/i.test(decoded);
    const message = isAccountExists
      ? 'An account with this email already exists. Please sign in with your email and password.'
      : decoded || 'Sign-in failed. Please try again.';
    navigate('/login', { replace: true, state: { oauthError: message } });
  }, [location.search, navigate]);

  return <HansePayLoader variant='splash' />;
}

function ForceLogoutFromUrlHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setHasAccessToken } = useCognitoAuthContext();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldForceLogout = (params.get('force_logout') || '').toLowerCase() === 'true';
    if (!shouldForceLogout) return;

    (async () => {
      params.delete('force_logout');
      const nextSearch = params.toString();

      if (typeof setHasAccessToken === 'function') {
        setHasAccessToken(false);
      }

      await oauthLogout();

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
          hash: location.hash,
        },
        { replace: true },
      );
    })();
  }, [location.pathname, location.search, location.hash, navigate, setHasAccessToken]);

  return null;
}

function App() {
  const dispatch = useDispatch();
  const { hasAccessToken, isCheckingSession } = useCognitoAuthContext();

  useEffect(() => {
    reapplyAmplifyOAuthConfig();
  }, []);

  useEffect(() => {
    dispatch(configActions.getConfig());
    dispatch(configActions.getReferenceCountries());
  }, [dispatch]);

  useEffect(() => {
    if (hasAccessToken) {
      dispatch(userActions.getUser());
      dispatch(userActions.getUserPreferences());
    } else {
      dispatch(userActions.clearUser());
    }
  }, [hasAccessToken, dispatch]);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <OAuthCallbackHandler />
      <ForceLogoutFromUrlHandler />
      <div className='bg-[#EBF4FB] flex-1 min-h-[100vh]'>
        <Routes>
          <Route index path='/' element={<Navigate to='/dashboard' />} />
          <Route path='/login' element={<Login />} />
          <Route path='/oauth-login' element={<OAuthCallbackPage />} />
          <Route path='/oauth-logout' element={<Navigate to='/login' replace />} />
          <Route path='/forgot-password' element={<ForgotPassword />} />
          <Route path='/email-start-kyc-process' element={<EmailStartKycProcess />} />
          <Route path='/verification-link-sent' element={<VerificationLinkSent />} />
          <Route path='/register' element={<Register />} />
          <Route path='/confirm-email' element={<ConfirmEmail />} />
          <Route path='/blindpay-accept' element={<BlindPayAccept />} />
          <Route element={<AccessTokenOutlet />}>
            <Route element={<OnboardingOutlet />}>
              <Route path='/verify-phone-number' element={<PhoneVerification />} />
              <Route path='/choose-user-type' element={<ChooseUserType />} />
              <Route path='/collect-data' element={<CollectData />} />
              <Route path='/financial' element={<Financial />} />
              <Route path='/compliance' element={<Compliance />} />
              <Route path='/collect-company-person-data' element={<CollectCompanyPersonData />} />
              <Route path='/accept-legal-agreements' element={<TermsAndConditions />} />
              <Route path='/perform-kyc' element={<VerificationPending />} />
              <Route path='/under-review' element={<UnderReview />} />
              <Route path='/account' element={<Account />} />
              <Route path='/account-settings' element={<Account />} />
              <Route path='/dashboard' element={<Dashboard />} />
              <Route path='/accounts' element={<Accounts />} />
              <Route path='/send-money' element={<SendMoney />} />
              <Route path='/send-money/:recipientId' element={<SendMoney />} />
              <Route path='/transaction-progress' element={<TransactionProgress />} />
              <Route path='/transaction-progress/:requestId' element={<TransactionProgress />} />
              <Route path='/recipients' element={<Recipients />} />
              <Route path='/recipients/:recipientId' element={<RecipientDetail />} />
              <Route path='/transaction-history' element={<TransactionHistory />} />
              <Route path='/transaction-history/:requestId' element={<TransactionDetail />} />
              <Route path='/select-currency' element={<SelectCurrency />} />
              <Route path='/recipient-type' element={<SelectRecipientType />} />
              <Route path='/select-rail' element={<SelectRail />} />
              <Route path='/add-recipient-form' element={<AddRecipientForm />} />
            </Route>
          </Route>
          <Route
            path='*'
            element={
              <NotFound details='The page you are looking for was moved,\nremoved or might have never existed.' buttonText='Back to the Home Page' />
            }
          />
        </Routes>
      </div>
      {(!isCheckingSession &&
        (window.location?.pathname.includes('/login') || window.location?.pathname.includes('register') || !hasAccessToken)) && <Footer />}
    </BrowserRouter>
  );
}

export default App;
